/**
 * Task Service Hooks
 * React hooks for accessing task management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all task-related services
 * @returns TaskServices object containing all task service instances
 */
export function useTaskServices() {
    const { services } = useCCMS();
    if (!services?.task) {
        throw new Error('Task services not available. Ensure CCMSProvider is configured with task services.');
    }
    return services.task;
}
/**
 * Hook to access TaskService
 */
export function useTaskService() {
    return useTaskServices().task;
}
/**
 * Hook to access TaskStatusService
 */
export function useTaskStatusService() {
    return useTaskServices().taskStatus;
}
/**
 * Hook to access TaskPriorityService
 */
export function useTaskPriorityService() {
    return useTaskServices().taskPriority;
}
/**
 * Hook to access TaskProgressStageService
 */
export function useTaskProgressStageService() {
    return useTaskServices().taskProgressStage;
}
/**
 * Hook to access TaskAssignmentService
 */
export function useTaskAssignmentService() {
    return useTaskServices().taskAssignment;
}
/**
 * Hook to access TaskAttachmentService
 */
export function useTaskAttachmentService() {
    return useTaskServices().taskAttachment;
}
/**
 * Hook to access TaskCommentService
 */
export function useTaskCommentService() {
    return useTaskServices().taskComment;
}
/**
 * Hook to access TaskRecurrenceService
 * Recurrence rules + series control for recurring tasks.
 */
export function useTaskRecurrenceService() {
    return useTaskServices().taskRecurrence;
}
/**
 * Hook to access TaskNoteService
 */
export function useTaskNoteService() {
    return useTaskServices().taskNote;
}
/**
 * Hook to access TaskLogService
 */
export function useTaskLogService() {
    return useTaskServices().taskLog;
}
/**
 * Hook to access LogTypeService
 */
export function useLogTypeService() {
    return useTaskServices().logType;
}
/**
 * Hook to access TaskNotificationService
 */
export function useTaskNotificationService() {
    return useTaskServices().taskNotification;
}
/**
 * Hook to access TaskNotificationQueueService
 */
export function useTaskNotificationQueueService() {
    return useTaskServices().taskNotificationQueue;
}
/**
 * Hook to access TaskReportService
 * Used for admin task reporting and analytics
 */
export function useTaskReportService() {
    return useTaskServices().taskReport;
}
/**
 * Hook to access TaskSettingsService
 * Used for admin task system settings
 */
export function useTaskSettingsService() {
    return useTaskServices().taskSettings;
}
/**
 * Hook to access TaskNotificationChannelService
 * Used for the per-NotificationType channel toggle admin tab.
 */
export function useTaskNotificationChannelService() {
    return useTaskServices().taskNotificationChannel;
}
/**
 * Hook to access TaskEscalationContactService
 * Used for the escalation-contacts admin tab.
 */
export function useTaskEscalationContactService() {
    return useTaskServices().taskEscalationContact;
}
//# sourceMappingURL=useTaskServices.js.map